import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { mediaList } from "@/mocks/media";
import { collectGenres, collectYears, filterMedia, getCategoryMeta, typeLabelMap } from "@/utils/media";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import MediaCard from "@/components/feature/MediaCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import type { MediaType } from "@/types/media";

const PAGE_SIZE = 8;
const REGIONS = ["全部", "日本", "韩国"];
const SORT_OPTIONS: { value: "latest" | "heat" | "rating"; label: string }[] = [
  { value: "latest", label: "最近更新" },
  { value: "heat", label: "热门度" },
  { value: "rating", label: "评分" },
];

interface FilterGroupProps {
  title: string;
  options: string[];
  active: string;
  onSelect: (value: string) => void;
}

function FilterGroup({ title, options, active, onSelect }: FilterGroupProps) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
      <span className="w-14 shrink-0 pt-1.5 text-xs text-foreground-500">{title}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onSelect(option)}
            className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1 text-xs transition-colors ${
              active === option
                ? "bg-primary-950 font-medium text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900"
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const [searchParams] = useSearchParams();
  const meta = getCategoryMeta(type);
  const isAll = type === "all";
  const activeType = meta?.type as MediaType | undefined;

  const [genres, setGenres] = useState<string[]>(() => {
    const genre = searchParams.get("genre");
    return genre ? [genre] : [];
  });
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState<"latest" | "heat" | "rating">("latest");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const genreParam = searchParams.get("genre");

useEffect(() => {
    setGenres(genreParam ? [genreParam] : []);
    setRegion("全部");
    setYear("全部");
    setSort("latest");
    setPage(1);
  }, [type, genreParam]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const pool = useMemo(
    () => (activeType ? mediaList.filter((item) => item.type === activeType) : mediaList),
    [activeType],
  );
  const genreOptions = useMemo(() => collectGenres(mediaList, activeType), [activeType]);
  const yearOptions = useMemo(() => collectYears(pool).slice(0, 6), [pool]);

  const filtered = useMemo(
    () =>
      filterMedia(mediaList, {
        type: activeType,
        genres,
        region,
        year,
        sort,
      }),
    [activeType, genres, region, year, sort],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const title = isAll ? "日韩影视大全" : meta ? meta.label : "影视分类";
  const description = meta
    ? meta.description
    : "日韩电影、电视剧与综艺的完整资料库，按类型、地区与年份筛选，快速找到想看的内容。";

  useDocumentMeta(
    `${title}大全 - 最新热门日韩影视作品 | 日韩影视大全`,
    `${description} 当前共收录 ${pool.length} 部作品，支持类型、地区、年份筛选与排序。`,
    `日韩${meta ? typeLabelMap[meta.type] : "影视"},日韩影视,${title},日韩影视推荐`,
  );

  const activeFilterCount = genres.length + (region !== "全部" ? 1 : 0) + (year !== "全部" ? 1 : 0);

  const resetFilters = () => {
    setGenres([]);
    setRegion("全部");
    setYear("全部");
    setSort("latest");
    setPage(1);
  };

  const toggleGenre = (genre: string) => {
    setGenres((current) =>
      current.includes(genre) ? current.filter((item) => item !== genre) : [...current, genre],
    );
    setPage(1);
  };

  const filterContent = (
    <>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <span className="w-14 shrink-0 pt-1.5 text-xs text-foreground-500">类型</span>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => {
              setGenres([]);
              setPage(1);
            }}
            className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1 text-xs transition-colors ${
              genres.length === 0
                ? "bg-primary-950 font-medium text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900"
            }`}
          >
            全部
          </button>
          {genreOptions.map((genre) => (
            <button
              key={genre}
              type="button"
              onClick={() => toggleGenre(genre)}
              className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1 text-xs transition-colors ${
                genres.includes(genre)
                  ? "bg-primary-950 font-medium text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900"
              }`}
            >
              {genre}
            </button>
          ))}
        </div>
      </div>

      <FilterGroup
        title="地区"
        options={REGIONS}
        active={region}
        onSelect={(value) => {
          setRegion(value);
          setPage(1);
        }}
      />

      <FilterGroup
        title="年份"
        options={["全部", ...yearOptions.map(String)]}
        active={year}
        onSelect={(value) => {
          setYear(value);
          setPage(1);
        }}
      />
    </>
  );

  if (type !== "all" && !meta) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 py-20 md:px-8">
        <EmptyState
          icon="ri-error-warning-line"
          title="没有找到这个分类"
          description="你访问的分类可能已更改或不存在，可以回到首页重新选择电影、电视剧或综艺。"
        >
          <Link
            to="/"
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
          >
            返回首页
          </Link>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="w-full pb-16">
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={
            activeType === "tv"
              ? "https://readdy.ai/api/search-image?query=Atmospheric%20wide%20shot%20of%20a%20quiet%20Tokyo%20apartment%20window%20at%20night%20with%20warm%20amber%20lamp%20glow%2C%20blue%20hour%20city%20skyline%20bokeh%2C%20deep%20navy%20shadows%2C%20cinematic%20still%20with%20soft%20film%20grain%2C%20intimate%20moody%20tone%2C%20no%20text&width=1600&height=700&seq=rk-cat-tv&orientation=landscape"
              : activeType === "variety"
                ? "https://readdy.ai/api/search-image?query=Warm%20evening%20rooftop%20scene%20with%20string%20lights%20and%20a%20long%20wooden%20table%2C%20soft%20amber%20glow%20against%20deep%20navy%20dusk%20sky%2C%20cozy%20gathering%20atmosphere%2C%20cinematic%20wide%20composition%2C%20gentle%20film%20grain%2C%20no%20text&width=1600&height=700&seq=rk-cat-variety&orientation=landscape"
                : "https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20an%20empty%20old%20movie%20theater%20with%20deep%20navy%20seats%2C%20a%20glowing%20amber%20screen%2C%20dust%20in%20the%20projector%20beam%2C%20dramatic%20atmospheric%20lighting%2C%20film%20noir%20mood%2C%20textured%20walls%2C%20no%20text&width=1600&height=700&seq=rk-cat-movie&orientation=landscape"
          }
          alt={`${title}氛围配图`}
          title={`${title} - 日韩影视分类`}
          className="absolute inset-0 h-full w-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 rk-hero-mask" />
        <div className="relative mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-background-300">
            <Link to="/" className="cursor-pointer transition-colors hover:text-accent-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-sm text-background-400" />
            {isAll ? (
              <span className="text-background-100">全部影视</span>
            ) : (
              <>
                <span>分类</span>
                <i className="ri-arrow-right-s-line text-sm text-background-400" />
                <span className="text-background-100">{title}</span>
              </>
            )}
          </nav>

          <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.22em] text-accent-300">
                <span className="h-px w-6 bg-accent-500" />
                {isAll ? "All Titles" : meta?.enLabel}
              </p>
              <h1 className="mt-2 font-heading text-2xl font-semibold text-background-50 md:text-4xl">
                {title}大全
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-background-300">{description}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="rounded-full border border-background-50/25 px-3 py-1.5 text-xs text-background-100">
                共 <span className="font-semibold text-accent-300">{pool.length}</span> 部作品
              </span>
              <span className="rounded-full border border-background-50/25 px-3 py-1.5 text-xs text-background-100">
                已筛选 {filtered.length} 部
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8">
        <div className="mt-6 md:mt-8">
          <div className="hidden rounded-lg border border-background-200/80 bg-background-50 p-4 md:p-5 lg:block">
            <div className="flex flex-col gap-3">
              {filterContent}
              <div className="flex flex-col gap-2 border-t border-background-200/70 pt-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500">排序</span>
                  <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setSort(option.value);
                          setPage(1);
                        }}
                        className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                          sort === option.value
                            ? "bg-background-50 font-medium text-primary-700"
                            : "text-foreground-500 hover:text-foreground-900"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={resetFilters}
                  disabled={activeFilterCount === 0 && sort === "latest"}
                  className="inline-flex w-fit cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-refresh-line text-sm" />
                  重置筛选
                </button>
              </div>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3 lg:hidden">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs font-medium text-foreground-800"
              >
                <i className="ri-filter-3-line text-base text-primary-700" />
                筛选
                {activeFilterCount > 0 && (
                  <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-semibold text-primary-950">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <select
                value={sort}
                onChange={(event) => {
                  setSort(event.target.value as "latest" | "heat" | "rating");
                  setPage(1);
                }}
                aria-label="排序方式"
                className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-2.5 py-2 text-xs text-foreground-800 outline-none"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <span className="text-[11px] text-foreground-500">共 {filtered.length} 部</span>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {genres.map((genre) => (
              <button
                key={genre}
                type="button"
                onClick={() => toggleGenre(genre)}
                className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-[11px] text-primary-800 transition-colors hover:bg-primary-200"
              >
                类型：{genre}
                <i className="ri-close-line text-xs" />
              </button>
            ))}
            {region !== "全部" && (
              <button
                type="button"
                onClick={() => {
                  setRegion("全部");
                  setPage(1);
                }}
                className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                地区：{region}
                <i className="ri-close-line text-xs" />
              </button>
            )}
            {year !== "全部" && (
              <button
                type="button"
                onClick={() => {
                  setYear("全部");
                  setPage(1);
                }}
                className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-[11px] text-accent-900 transition-colors hover:bg-accent-200"
              >
                年份：{year}
                <i className="ri-close-line text-xs" />
              </button>
            )}
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={resetFilters}
                className="cursor-pointer text-[11px] text-foreground-500 underline-offset-2 hover:text-primary-700 hover:underline"
              >
                清除全部筛选
              </button>
            )}
          </div>
        </div>

        <div className="mt-6">
          {paged.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 xl:grid-cols-4">
                {paged.map((item) => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>
              <div className="mt-8 flex flex-col items-center gap-3">
                <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
                <p className="text-[11px] text-foreground-400">
                  第 {safePage} / {totalPages} 页 · 每页 {PAGE_SIZE} 部作品
                </p>
              </div>
            </>
          ) : (
            <EmptyState
              title="没有匹配的影视作品"
              description="当前筛选组合下暂时没有内容，试试减少筛选条件，或查看其他分类。"
            >
              <button
                type="button"
                onClick={resetFilters}
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
              >
                重置筛选条件
              </button>
              <Link
                to="/category/all"
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                查看全部作品
              </Link>
            </EmptyState>
          )}
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-primary-950/50"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="rk-fade-up absolute inset-x-0 bottom-0 max-h-[86vh] overflow-y-auto rounded-t-2xl bg-background-50 px-5 pb-6 pt-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-base font-semibold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭筛选"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-600"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="mt-4 flex flex-col gap-4">{filterContent}</div>
            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-950 px-4 py-2.5 text-sm font-medium text-background-50"
              >
                查看 {filtered.length} 部结果
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}